import { useState } from 'react';
import { CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';

import { checkDiscountCode, checkGiftCard } from 'api/jalapeno';
import { usePromoCodeContext } from 'contexts/PromoCodeContext';
import Card from 'honeydew/Card';
import TextField from 'honeydew/TextField';
import { trackEvent } from 'utils/analyticsUtils';
import { logMessage } from 'utils/sentryUtils';
import { getSignupLocalStorage, setSignupLocalStorage } from 'utils/signupUtils';

const PromoCodeType = Object.freeze({
    DISCOUNT: 'discount',
    GIFT: 'gift',
});

/**
 * Displays a form for applying a promo and gift card codes manually
 * @returns {JSX.Element}
 */
function PromoCodeForm() {
    const { appliedPromoCodeData, persistAppliedPromoCode, setAppliedPromoCodeData } = usePromoCodeContext();
    const location = useLocation();

    const [inputValue, setInputValue] = useState('');
    const [submissionError, setSubmissionError] = useState(null);

    const [giftCode, setGiftCode] = useState(getSignupLocalStorage()?.giftCode ?? null);

    const handleChange = (e) => {
        setInputValue(e.target.value.toUpperCase());
    };

    const handlePromoCodeSubmission = async (submittedPromoCode) => {
        try {
            const response = await checkDiscountCode(null, submittedPromoCode);

            if (response?.data?.disc?.valid) {
                setAppliedPromoCodeData(response.data);
                persistAppliedPromoCode(submittedPromoCode);
                setInputValue('');

                trackEvent('promo code apply', {
                    promo_code: submittedPromoCode,
                    pathname: location.pathname,
                    method: 'manual',
                });
            } else {
                setSubmissionError(`Promo code ${submittedPromoCode} is not valid.`);
            }

            setInputValue('');
        } catch (error) {
            logMessage('Error handling promo code form submission', {
                level: 'warning',
                extra: { error: error?.message ?? error },
            });
        }
    };

    const handleGiftCodeSubmission = async (submittedGiftCardCode) => {
        try {
            const response = await checkGiftCard({ giftCardCode: submittedGiftCardCode });
            if (response?.data?.giftCode?.status === 'AVAILABLE') {
                setGiftCode(response.data.giftCode.code);
                setSignupLocalStorage({ giftCode: response.data.giftCode.code });
                setInputValue('');

                trackEvent('promo code apply', {
                    promo_code: response.data.giftCode.code,
                    pathname: location.pathname,
                    method: 'manual',
                });
            } else {
                setSubmissionError(`Promo code ${submittedGiftCardCode} is not valid.`);
            }
        } catch (error) {
            logMessage('Error handling gift code form submission', {
                level: 'warning',
                extra: { error: error?.message ?? error },
            });
        }
    };

    const handleFormSubmission = async (e) => {
        e.preventDefault();

        setSubmissionError(null);

        if (inputValue.startsWith('GIFT-')) {
            handleGiftCodeSubmission(inputValue);
        } else {
            handlePromoCodeSubmission(inputValue);
        }
    };

    const handleDelete = ({ type }) => {
        let codeToDelete;

        if (type === PromoCodeType.DISCOUNT) {
            codeToDelete = appliedPromoCodeData?.disc?.code;
            setAppliedPromoCodeData(undefined);
            persistAppliedPromoCode('');
        } else {
            codeToDelete = giftCode;
            setGiftCode(null);
            setSignupLocalStorage({ giftCode: null });
        }

        trackEvent('promo code removed', {
            promo_code: codeToDelete,
            pathname: location.pathname,
            method: 'manual',
        });
    };

    return (
        <Card extendedStyles={{ padding: 'px-20 pt-25 pb-15' }}>
            <h2 className="font-grotesk-bold text-body-mobile-lg">Have a promo code?</h2>
            <form onSubmit={handleFormSubmission} className="relative">
                <TextField
                    id="promo-code-input"
                    inputProps={{ className: 'with-apply-btn' }}
                    label="Promo code"
                    name="promoCode"
                    placeholder="Promo code"
                    onChange={handleChange}
                    value={inputValue}
                    error={!!submissionError}
                    errorMsg={submissionError}
                    required
                />
                <button
                    className="text-brand-primary absolute right-[1px] top-[1px] flex h-[54px] items-center rounded-br-[5px] rounded-tr-[5px] bg-white pl-10 pr-20 font-grotesk-bold text-body-mobile-md disabled:text-grey"
                    type="submit"
                >
                    Apply
                </button>
            </form>
            <div className="flex flex-col">
                {appliedPromoCodeData?.disc?.code && (
                    <AppliedCode
                        code={appliedPromoCodeData?.disc?.code}
                        onDelete={() => handleDelete({ type: PromoCodeType.DISCOUNT })}
                    />
                )}
                {giftCode && (
                    <AppliedCode code={giftCode} onDelete={() => handleDelete({ type: PromoCodeType.GIFT })} />
                )}
            </div>
        </Card>
    );
}

function AppliedCode({ code, onDelete }) {
    return (
        <div className="text-body-md text-brand-primary mb-10 flex items-center gap-10 font-grotesk-bold">
            <CheckIcon className="w-15 stroke-[3px]" />
            {code}
            <button onClick={onDelete} className="ml-auto" type="button" aria-label={`Remove promo code ${code}`}>
                <TrashIcon className="w-20 text-black" />
            </button>
        </div>
    );
}

AppliedCode.propTypes = {
    code: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
};

export { PromoCodeForm };
